import { createContext, useContext } from "react"

export interface SponsorSession {
  id: string
  email: string
  name: string
  phone: string | null
  city: string | null
  givingSchedule: string
  emailUpdates: boolean
  physicalLetters: boolean
  publicName: boolean
  tier: string
  status: string
  since: number
  monthlyCommitment: number | null
  nextExpectedAt: string | null
  lifetimeDonated: number
}

export interface SponsorUpdate {
  id: string
  childId: string
  childName: string
  date: string
  description: string
  url: string
}

export interface SponsorDocument {
  name: string
  purpose: string
  contentType: string
  sizeBytes: number
  uploadedAt: string
  url: string
}

export interface SponsorChild {
  id: string
  code: string
  name: string
  age: number
  classLevel: string
  school: string
  since: string | null
  photoUrl: string | null
  documents: SponsorDocument[]
  updates: SponsorUpdate[]
}

export interface SponsorAcademicResult {
  id: string
  subject: { id: string; name: string }
  score: number | null
  grade: string | null
  proficiency: string | null
}

export interface SponsorAcademicTerm {
  id: string
  session: string
  term: string
  attendance: number | null
  position: string | null
  teacherSummary: string | null
  results: SponsorAcademicResult[]
}

export interface SponsorDonation {
  id: string
  code: string
  receiptCode: string
  amount: number
  currency: string
  designation: string
  method: string
  status: string
  childName: string | null
  donatedAt: string
}

export type SponsorshipRequestStatus = "submitted" | "reviewing" | "approved" | "declined" | "closed"

export interface SponsorshipRequest {
  id: string
  type: "sponsorship"
  status: SponsorshipRequestStatus
  message: string
  createdAt: string
  updatedAt: string
}

export interface SponsorPortal {
  sponsor: SponsorSession
  stats: {
    childrenSponsored: number
    totalGiven: number
    nextContribution: string | null
  }
  children: SponsorChild[]
  recentUpdates: SponsorUpdate[]
  donations: SponsorDonation[]
  sponsorshipRequest: SponsorshipRequest | null
  matchState: "none" | "awaiting_match" | "matched" | "rematch_needed"
  activeMatchCount: number
}

export interface SponsorPortalContextValue {
  data: SponsorPortal
  refresh: () => Promise<void>
}

export const SponsorSessionContext = createContext<SponsorSession | null>(null)
export const SponsorPortalContext = createContext<SponsorPortalContextValue | null>(null)

export function useSponsorSession(): SponsorSession | null {
  return useContext(SponsorSessionContext)
}

export function useSponsorPortal(): SponsorPortal | null {
  return useContext(SponsorPortalContext)?.data ?? null
}

export function useRefreshSponsorPortal(): (() => Promise<void>) | null {
  return useContext(SponsorPortalContext)?.refresh ?? null
}
